import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import type { ClosingTimeRequest } from '../types'
import { type RestaurantStatus, type ClosedReason } from '../status'
import {
  useRestaurantStatus,
  type RestaurantStatusResponse,
  restaurantStatusQueryKey,
  broadcastRestaurantStatusSync,
} from './useRestaurantStatus'

export type { RestaurantStatus, ClosedReason }

const MAX_CLOCK_DRIFT_MS = 5 * 60 * 1000
const BEGIN_TIME_SKEW_BUFFER_MS = 5 * 1000

export interface QuickStatusState {
  status: RestaurantStatus
  isLoading: boolean
  activeClosureId: number | null
  activeClosureEnd: Date | null
  activeClosureComment: string | null
  remainingTime: string | null
  closedReason: ClosedReason | null
  canOpenNow: boolean
}

/**
 * Hook for quick restaurant status management.
 * Uses canonical backend status endpoint and keeps the existing action API.
 */
export function useQuickStatus(restaurantId: number) {
  const queryClient = useQueryClient()
  const statusQuery = useRestaurantStatus(restaurantId)
  const { t, i18n } = useTranslation('quickStatus')
  // Mutex to prevent concurrent toggle operations from racing each other.
  // (e.g. fast-clicking the inline 3-way toggle before isPending propagates)
  const toggleInFlightRef = useRef(false)

  const getEffectiveNowMs = () => {
    const localNowMs = Date.now()
    const serverNowMs = statusQuery.data?.serverTime

    if (!serverNowMs) {
      return localNowMs
    }

    const driftMs = serverNowMs - localNowMs
    const clampedDriftMs = Math.max(
      -MAX_CLOCK_DRIFT_MS,
      Math.min(MAX_CLOCK_DRIFT_MS, driftMs)
    )

    return localNowMs + clampedDriftMs
  }

  const applyOptimisticClosedStatus = (
    endTimeMs: number,
    comment: string,
    deliveryOnly: boolean,
    activeClosureId: number | null
  ) => {
    queryClient.setQueryData<RestaurantStatusResponse>(
      restaurantStatusQueryKey(restaurantId),
      (previous) => ({
        ...(previous ?? {}),
        status: deliveryOnly ? 'delivery_only' : 'closed',
        closedReason: deliveryOnly ? null : 'temporary_closure',
        activeClosureId,
        activeClosureEnd: endTimeMs,
        activeClosureComment: comment,
        canOpenNow: true,
        serverTime: Date.now(),
      })
    )
  }

  const invalidateStatusQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: restaurantStatusQueryKey(restaurantId) }),
      queryClient.invalidateQueries({ queryKey: ['closing-times', restaurantId] }),
    ])
    await queryClient.refetchQueries({
      queryKey: restaurantStatusQueryKey(restaurantId),
      type: 'active',
    })
  }

  const addClosingTime = useMutation({
    mutationFn: async (request: ClosingTimeRequest) => {
      const response = await apiClient.post<{ newClosingTimeId?: number }>(
        '/addClosingTime',
        request
      )
      return response.data
    },
    onSuccess: invalidateStatusQueries,
  })

  const removeClosingTime = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.post('/removeClosingTime', { id })
    },
    onSuccess: invalidateStatusQueries,
  })

  const state: QuickStatusState = useMemo(() => {
    const data = statusQuery.data
    const activeClosureEnd = data?.activeClosureEnd
      ? new Date(data.activeClosureEnd)
      : null

    const remainingTime = activeClosureEnd
      ? formatRemainingTime(activeClosureEnd, i18n.language, t)
      : null

    return {
      status: data?.status ?? 'closed',
      isLoading: statusQuery.isLoading,
      activeClosureId: data?.activeClosureId ?? null,
      activeClosureEnd,
      activeClosureComment: data?.activeClosureComment ?? null,
      remainingTime,
      closedReason: data?.closedReason ?? null,
      canOpenNow: data?.canOpenNow ?? false,
    }
  }, [statusQuery.data, statusQuery.isLoading, i18n.language, t])

  const refreshStatus = async () => {
    await statusQuery.refetch()
  }

  /**
   * Close the restaurant immediately until a specified end time.
   * Default: closes for 1 hour.
   */
  const closeNow = async (options?: {
    endTime?: Date
    comment?: string
    deliveryOnly?: boolean
  }) => {
    const nowMs = getEffectiveNowMs()
    const beginTimeMs = nowMs - BEGIN_TIME_SKEW_BUFFER_MS
    const fallbackEndTimeMs = nowMs + 60 * 60 * 1000
    const endTimeMs = Math.max(options?.endTime?.getTime() ?? fallbackEndTimeMs, beginTimeMs + 60 * 1000)
    const comment = options?.comment ?? 'Temporarily closed'
    const deliveryOnly = options?.deliveryOnly ?? false

    const result = await addClosingTime.mutateAsync({
      restaurantId,
      beginTime: beginTimeMs,
      endTime: endTimeMs,
      comment,
      deliveryOnly,
      showEndTime: true,
    })
    applyOptimisticClosedStatus(
      endTimeMs,
      comment,
      deliveryOnly,
      result.newClosingTimeId ?? statusQuery.data?.activeClosureId ?? null
    )
    broadcastRestaurantStatusSync(restaurantId)
    await refreshStatus()
  }

  /**
   * Close the restaurant until end of day.
   */
  const closeUntilEndOfDay = async (options?: {
    comment?: string
    deliveryOnly?: boolean
  }) => {
    const nowMs = getEffectiveNowMs()
    const beginTimeMs = nowMs - BEGIN_TIME_SKEW_BUFFER_MS
    const endOfDay = new Date(nowMs)
    endOfDay.setHours(23, 59, 59, 999)
    const endTimeMs = Math.max(endOfDay.getTime(), beginTimeMs + 60 * 1000)
    const comment = options?.comment ?? 'Closed for the day'
    const deliveryOnly = options?.deliveryOnly ?? false

    const result = await addClosingTime.mutateAsync({
      restaurantId,
      beginTime: beginTimeMs,
      endTime: endTimeMs,
      comment,
      deliveryOnly,
      showEndTime: true,
    })
    applyOptimisticClosedStatus(
      endTimeMs,
      comment,
      deliveryOnly,
      result.newClosingTimeId ?? statusQuery.data?.activeClosureId ?? null
    )
    broadcastRestaurantStatusSync(restaurantId)
    await refreshStatus()
  }

  /**
   * Atomically transition the restaurant's status to one of: open, closed,
   * or delivery_only (delivery paused, pickup still open).
   *
   * Calls the transactional backend endpoint POST
   * /api/admin/restaurants/:id/quick-status, which inside a single DB
   * transaction:
   *   1. removes every currently-active closure for this restaurant
   *   2. (if target != "open") inserts a new 365-day closure with the
   *      appropriate deliveryOnly flag
   *
   * No intermediate "two stacked closures" state is ever observable, even
   * under concurrent writes from other admins. The latest writer wins.
   *
   * The legacy /addClosingTime and /removeClosingTime endpoints are kept
   * intact because the production RestaurantApp Android client still uses them.
   *
   * Local mutex (toggleInFlightRef) is a UX safeguard against rapid clicks
   * within this component before React's isPending state propagates; the
   * server is the source of truth for cross-client consistency.
   */
  const setToggleStatus = async (
    target: 'open' | 'closed' | 'delivery_only',
    comment?: string
  ): Promise<boolean> => {
    if (toggleInFlightRef.current) return false
    toggleInFlightRef.current = true
    try {
      const response = await apiClient.post<{
        target: string
        removedClosureIds: number[]
        newClosingTimeId: number | null
      }>(`/api/admin/restaurants/${restaurantId}/quick-status`, {
        target,
        comment: comment ?? null,
      })

      if (target !== 'open') {
        const nowMs = getEffectiveNowMs()
        const startOfToday = new Date(nowMs)
        startOfToday.setHours(0, 0, 0, 0)
        const endTimeMs =
          startOfToday.getTime() + 365 * 24 * 60 * 60 * 1000
        const deliveryOnly = target === 'delivery_only'
        const finalComment =
          comment ?? (deliveryOnly ? 'No delivery' : 'Closed')

        applyOptimisticClosedStatus(
          endTimeMs,
          finalComment,
          deliveryOnly,
          response.data.newClosingTimeId
        )
      } else {
        queryClient.setQueryData<RestaurantStatusResponse>(
          restaurantStatusQueryKey(restaurantId),
          (previous) =>
            previous
              ? {
                  ...previous,
                  activeClosureId: null,
                  activeClosureEnd: null,
                  activeClosureComment: null,
                  serverTime: Date.now(),
                }
              : previous
        )
      }

      // Invalidate caches so dependent UI (e.g. closures list) refetches.
      await invalidateStatusQueries()
      broadcastRestaurantStatusSync(restaurantId)
      return true
    } finally {
      toggleInFlightRef.current = false
    }
  }

  /**
   * Close the restaurant for a specific duration (in minutes).
   */
  const closeForDuration = async (
    durationMinutes: number,
    options?: {
      comment?: string
      deliveryOnly?: boolean
    }
  ) => {
    const nowMs = getEffectiveNowMs()
    const beginTimeMs = nowMs - BEGIN_TIME_SKEW_BUFFER_MS
    const endTimeMs = nowMs + durationMinutes * 60 * 1000
    const comment = options?.comment ?? `Temporarily closed for ${durationMinutes} minutes`
    const deliveryOnly = options?.deliveryOnly ?? false

    const result = await addClosingTime.mutateAsync({
      restaurantId,
      beginTime: beginTimeMs,
      endTime: endTimeMs,
      comment,
      deliveryOnly,
      showEndTime: true,
    })
    applyOptimisticClosedStatus(
      endTimeMs,
      comment,
      deliveryOnly,
      result.newClosingTimeId ?? statusQuery.data?.activeClosureId ?? null
    )
    broadcastRestaurantStatusSync(restaurantId)
    await refreshStatus()
  }

  /**
   * Immediately reopen the restaurant by removing the active closure.
   */
  const openNow = async () => {
    if (state.activeClosureId) {
      await removeClosingTime.mutateAsync(state.activeClosureId)
      broadcastRestaurantStatusSync(restaurantId)
      await refreshStatus()
      return true
    }
    return false
  }

  /**
   * Toggle delivery-only mode.
   * If closed, switches to delivery-only.
   * If delivery-only, reopens fully.
   * If open, enables delivery-only until end of day.
   */
  const toggleDeliveryOnly = async () => {
    if (state.status === 'delivery_only' && state.activeClosureId) {
      await removeClosingTime.mutateAsync(state.activeClosureId)
      broadcastRestaurantStatusSync(restaurantId)
      await refreshStatus()
      return
    }

    if (state.status === 'closed' && state.activeClosureId) {
      return
    }

    const nowMs = getEffectiveNowMs()
    const beginTimeMs = nowMs - BEGIN_TIME_SKEW_BUFFER_MS
    const endOfDay = new Date(nowMs)
    endOfDay.setHours(23, 59, 59, 999)
    const endTimeMs = Math.max(endOfDay.getTime(), beginTimeMs + 60 * 1000)

    const result = await addClosingTime.mutateAsync({
      restaurantId,
      beginTime: beginTimeMs,
      endTime: endTimeMs,
      comment: 'Delivery only',
      deliveryOnly: true,
      showEndTime: true,
    })
    applyOptimisticClosedStatus(
      endTimeMs,
      'Delivery only',
      true,
      result.newClosingTimeId ?? statusQuery.data?.activeClosureId ?? null
    )
    broadcastRestaurantStatusSync(restaurantId)
    await refreshStatus()
  }

  return {
    ...state,
    closeNow,
    closeUntilEndOfDay,
    closeForDuration,
    openNow,
    toggleDeliveryOnly,
    setToggleStatus,
    isClosing: addClosingTime.isPending,
    isOpening: removeClosingTime.isPending,
    refetch: refreshStatus,
  }
}

/**
 * Format remaining time in a human-readable format.
 * Examples: "45 min", "2h 30m", "Until 18:00"
 */
function formatRemainingTime(
  endTime: Date,
  language: string,
  translate: (key: string, options?: Record<string, unknown>) => string
): string {
  const now = new Date()
  const diffMs = endTime.getTime() - now.getTime()

  if (diffMs <= 0) return translate('time.expired')

  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMinutes / 60)
  const remainingMinutes = diffMinutes % 60

  if (diffHours === 0) {
    return translate('time.minutesShort', { count: diffMinutes })
  }

  if (diffHours < 6) {
    if (remainingMinutes === 0) {
      return translate('time.hoursShort', { count: diffHours })
    }
    return translate('time.hoursMinutesShort', {
      hours: diffHours,
      minutes: remainingMinutes,
    })
  }

  return translate('time.until', {
    time: endTime.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' }),
  })
}
